import { useEffect, useMemo, useState } from 'react';
import { Hash, Search, X } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/components/AuthProvider';
import { useChat } from '@/contexts/ChatContext';
import { teamMemberInitials } from '@/components/teams/teamManagementChrome';
import { cn } from '@/lib/utils';

export type MessagingPane = 'team' | 'dm';

type Props = {
  pane: MessagingPane;
  teamRoomId: string;
  teamChannelName: string;
  selectedMemberId: string | null;
  pendingInviteIds?: Set<string>;
  onSelectTeam: () => void;
  onSelectMember: (memberId: string) => void;
};

function UnreadBadge({ count }: { count: number }) {
  if (!count) return null;
  return (
    <span className="ml-1 inline-flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-brand px-1.5 text-[11px] font-semibold text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function previewText(content: string | undefined, isOwn: boolean): string {
  if (!content) return '';
  const prefix = isOwn ? 'You: ' : '';
  const body = content.length > 42 ? `${content.slice(0, 42)}…` : content;
  return `${prefix}${body}`;
}

export function TeamMessagingSidebar({
  pane,
  teamRoomId,
  teamChannelName,
  selectedMemberId,
  pendingInviteIds,
  onSelectTeam,
  onSelectMember,
}: Props) {
  const { user } = useAuth();
  const {
    teamMembers,
    unreadCounts,
    lastMessages,
    messages,
    isConnected,
    isConnecting,
    connect,
    isLoadingMembers,
  } = useChat();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    if (isConnected || isConnecting) return;
    connect();
  }, [user?.id, isConnected, isConnecting, connect]);

  const q = search.trim().toLowerCase();

  const lastTeamMessage = useMemo(() => {
    const roomMsgs = messages.filter((m) => m.room_id === teamRoomId);
    return roomMsgs.length ? roomMsgs[roomMsgs.length - 1] : null;
  }, [messages, teamRoomId]);

  const showTeam = !q || teamChannelName.toLowerCase().includes(q);

  const filteredMembers = useMemo(() => {
    return teamMembers
      .filter((member) => member.id !== user?.id)
      .filter((member) => {
        if (!q) return true;
        return (
          (member.full_name || '').toLowerCase().includes(q)
          || (member.email || '').toLowerCase().includes(q)
        );
      });
  }, [teamMembers, user?.id, q]);

  const teamUnread = unreadCounts[teamRoomId] || 0;

  return (
    <div className="flex h-full min-h-0 flex-col bg-white">
      <div className="shrink-0 border-b border-[#EAECF0] px-3 py-3">
        <div className="flex h-9 items-center gap-2 rounded-lg border border-[#E4E7EC] bg-[#F9FAFB] px-2.5">
          <Search className="h-4 w-4 shrink-0 text-[#98A2B3]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search chats..."
            className="min-w-0 flex-1 bg-transparent text-[13px] text-[#101828] outline-none placeholder:text-[#98A2B3]"
          />
          {search ? (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearch('')}
              className="text-[#98A2B3] hover:text-[#101828]"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 py-3">
        {showTeam ? (
          <div className="mb-4">
            <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#98A2B3]">
              Team
            </p>
            <button
              type="button"
              onClick={onSelectTeam}
              className={cn(
                'flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition',
                pane === 'team'
                  ? 'bg-brand-soft'
                  : 'hover:bg-[#F9FAFB]',
              )}
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand-cta">
                <Hash className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-semibold text-[#101828]">{teamChannelName}</p>
                <p className="truncate text-[12px] text-[#667085]">
                  {lastTeamMessage
                    ? previewText(lastTeamMessage.content, lastTeamMessage.sender_id === user?.id)
                    : 'Team conversation'}
                </p>
              </div>
              <UnreadBadge count={pane === 'team' ? 0 : teamUnread} />
            </button>
          </div>
        ) : null}

        <div>
          <p className="px-2 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#98A2B3]">
            Direct messages
          </p>
          {filteredMembers.map((member) => {
            const unread = unreadCounts[member.id] || 0;
            const last = lastMessages[member.id];
            const pending = pendingInviteIds?.has(member.id);
            const selected = pane === 'dm' && selectedMemberId === member.id;
            const subtitle = pending
              ? 'Invite pending'
              : last
                ? previewText(last.content, last.sender_id === user?.id)
                : member.is_online
                  ? 'Online'
                  : 'Direct message';
            return (
              <button
                key={member.id}
                type="button"
                onClick={() => onSelectMember(member.id)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-xl px-2 py-2 text-left transition',
                  selected ? 'bg-brand-soft' : 'hover:bg-[#F9FAFB]',
                )}
              >
                <div className="relative shrink-0">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={member.avatar_url} />
                    <AvatarFallback className="bg-[#F2F4F7] text-[11px] font-semibold text-[#475467]">
                      {teamMemberInitials(member.full_name || member.email || '?')}
                    </AvatarFallback>
                  </Avatar>
                  <span
                    className={cn(
                      'absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white',
                      member.is_online ? 'bg-[#12B76A]' : 'bg-[#D0D5DD]',
                    )}
                    aria-hidden
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-[#101828]">
                    {member.full_name || member.email}
                  </p>
                  <p className={cn(
                    'truncate text-[12px]',
                    pending ? 'text-[#B54708]' : unread > 0 ? 'font-medium text-[#344054]' : 'text-[#667085]',
                  )}
                  >
                    {subtitle}
                  </p>
                </div>
                <UnreadBadge count={selected ? 0 : unread} />
              </button>
            );
          })}
          {filteredMembers.length === 0 ? (
            <p className="px-2 py-6 text-center text-[13px] text-[#667085]">
              {q ? 'No chats match your search' : isLoadingMembers ? 'Loading members…' : 'No direct messages yet'}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
